import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getCourseDetail, GetCourseDetailOutputType } from 'zite-endpoints-sdk';
import { useFeatureToggles } from '@/components/FeatureToggleProvider';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, CheckCircle, Play, Lock, AlertTriangle, Clock, ChevronRight, Award, ClipboardCheck, BookOpen, ShieldAlert, Info } from 'lucide-react';

type Module = GetCourseDetailOutputType['modules'][0];

const statusConfig: Record<string, { icon: React.ReactNode; color: string; bg: string; label: string }> = {
  approved: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-primary', bg: 'bg-primary/15', label: 'Complete' },
  quiz_passed: { icon: <ClipboardCheck className="w-4 h-4" />, color: 'text-primary', bg: 'bg-primary/15', label: 'Quiz Passed' },
  learning_complete: { icon: <BookOpen className="w-4 h-4" />, color: 'text-primary', bg: 'bg-accent/20', label: 'Learning Done' },
  in_progress: { icon: <Play className="w-4 h-4" />, color: 'text-primary', bg: 'bg-accent/20', label: 'In Progress' },
  available: { icon: <Play className="w-4 h-4" />, color: 'text-foreground', bg: 'bg-muted', label: 'Ready' },
  further_training: { icon: <AlertTriangle className="w-4 h-4" />, color: 'text-destructive', bg: 'bg-destructive/15', label: 'Returned' },
  assessment_in_progress: { icon: <Clock className="w-4 h-4" />, color: 'text-primary', bg: 'bg-primary/10', label: 'Awaiting Assessment' },
  locked: { icon: <Lock className="w-3.5 h-3.5" />, color: 'text-muted-foreground', bg: 'bg-muted', label: 'Locked' },
};

function ModuleCard({ mod, enrollmentId, useV2 }: { mod: Module; enrollmentId: string; useV2?: boolean }) {
  const cfg = statusConfig[mod.status] || statusConfig.locked;
  const isLocked = mod.status === 'locked';
  const navigate = useNavigate();

  return (
    <button
      disabled={isLocked}
      onClick={() => !isLocked && navigate(useV2 ? `/train/${enrollmentId}/module/${mod.id}/v2` : `/train/${enrollmentId}/module/${mod.id}`)}
      className={`w-full text-left flex items-center gap-3 p-4 rounded-xl border-2 transition-all touch-manipulation ${
        isLocked
          ? 'opacity-50 border-border cursor-not-allowed'
          : mod.status === 'in_progress' || mod.status === 'available'
            ? 'border-primary/20 hover:border-primary/40 active:bg-muted/50 cursor-pointer'
            : 'border-border hover:border-primary/30 active:bg-muted/50 cursor-pointer'
      }`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cfg.bg} ${cfg.color}`}>
        {cfg.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-muted-foreground">{mod.displayOrder}</span>
          <span className="text-sm font-medium text-foreground truncate">{mod.displayLabel}</span>
        </div>
        <p className={`text-xs mt-0.5 ${cfg.color}`}>{mod.statusDetail}</p>
        {mod.estimatedMinutes > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <Clock className="w-3 h-3" /> ~{mod.estimatedMinutes} min
          </p>
        )}

        {/* Progress bar for in-progress modules */}
        {(mod.status === 'in_progress' || mod.status === 'learning_complete') && mod.sectionsTotal > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-muted rounded-full">
              <div className="h-1.5 bg-primary rounded-full transition-all"
                style={{ width: `${Math.round((mod.sectionsCompleted / mod.sectionsTotal) * 100)}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground">{mod.sectionsCompleted}/{mod.sectionsTotal}</span>
          </div>
        )}
      </div>
      {!isLocked && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
    </button>
  );
}

export default function CourseDetail() {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureToggles();
  const isV2Enabled = isEnabled('interactive-course-player');
  const [data, setData] = useState<GetCourseDetailOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !enrollmentId) return;
    getCourseDetail({ enrollmentId })
      .then(setData)
      .catch(e => setError(e?.message || 'Could not load course'))
      .finally(() => setLoading(false));
  }, [user, enrollmentId]);

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-6 w-40" /><Skeleton className="h-24" /><Skeleton className="h-4 w-full" />
      {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  if (error || !data) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center">
      <ShieldAlert className="w-12 h-12 text-destructive mx-auto mb-3" />
      <h2 className="font-semibold text-foreground mb-1">Access Denied</h2>
      <p className="text-sm text-muted-foreground mb-4">{error || 'This course is not available.'}</p>
      <Link to="/train" className="text-sm text-primary hover:underline">← Back to My Training</Link>
    </div>
  );

  const { course, modules, progress, enrollment } = data;
  const currentModule = modules.find(m => ['in_progress', 'available', 'learning_complete', 'quiz_passed'].includes(m.status));
  const totalMinutes = modules.reduce((s, m) => s + m.estimatedMinutes, 0);
  const isComplete = enrollment.status === 'Course Complete';

  return (
    <div className="max-w-lg mx-auto pb-8">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border bg-card">
        <Link to="/train" className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> My Training
        </Link>

        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{course.courseCode}</span>
          <span className="text-xs text-muted-foreground">v{course.currentVersion}</span>
        </div>
        <h1 className="text-xl font-bold text-foreground mb-1">{course.title}</h1>
        {course.description && <p className="text-sm text-muted-foreground leading-relaxed mb-3">{course.description}</p>}

        {/* Progress bar */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
            <div className="h-3 bg-primary rounded-full transition-all" style={{ width: `${progress.overallPercent}%` }} />
          </div>
          <span className="text-lg font-bold text-foreground">{progress.overallPercent}%</span>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>{progress.completedModules} of {progress.totalModules} modules</span>
          {totalMinutes > 0 && (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ~{totalMinutes} min total</span>
          )}
          {enrollment.dueDate && (
            <span className="text-destructive font-medium">
              Due: {new Date(enrollment.dueDate).toLocaleDateString('en-GB')}
            </span>
          )}
        </div>
      </div>

      {/* Continue button */}
      {currentModule && !isComplete && (
        <div className="px-4 pt-4">
          <button
            onClick={() => navigate(`/train/${enrollmentId}/module/${currentModule.id}`)}
            className="w-full bg-primary text-primary-foreground font-semibold py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform touch-manipulation"
          >
            <Play className="w-4 h-4" />
            Continue: {currentModule.displayOrder} — {currentModule.displayLabel}
          </button>
        </div>
      )}

      {/* Course info bar */}
      <div className="px-4 pt-4">
        <div className="bg-muted/50 rounded-xl p-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>Pass mark: {course.defaultPassMark}%</span>
          <span>{course.criticalQuestionRule ? 'Critical Qs: must all be correct' : 'Standard scoring'}</span>
          <span>{course.sequentialModules ? 'Sequential order' : 'Any order'}</span>
          <span>Review: {course.reviewPeriod}</span>
        </div>
      </div>

      {/* Modules list */}
      <div className="px-4 pt-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> Modules ({modules.length})
        </h2>
        <div className="space-y-2">
          {modules.map(m => (
            <ModuleCard key={m.id} mod={m} enrollmentId={enrollmentId!} useV2={isV2Enabled} />
          ))}
        </div>
      </div>

      {/* Sequential locking explanation */}
      {course.sequentialModules && modules.some(m => m.status === 'locked') && (
        <div className="px-4 pt-3">
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>This course requires sequential completion. Each module unlocks after the previous one is fully approved including the practical assessment.</span>
          </div>
        </div>
      )}

      {/* Course Completion / Certificate */}
      <div className="px-4 pt-6">
        {progress.completedModules === progress.totalModules && progress.totalModules > 0 ? (
          <button
            onClick={() => navigate(`/train/${enrollmentId}/completion`)}
            className={`w-full border-2 rounded-xl p-4 flex items-center gap-3 text-left active:scale-[0.98] transition-transform touch-manipulation ${
              isComplete ? 'border-primary bg-primary/5' : 'border-primary/40 bg-primary/5'
            }`}
          >
            <Award className="w-9 h-9 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">
                {isComplete ? 'View Certificate' : 'Course Completion'}
              </p>
              <p className="text-xs text-muted-foreground">
                {isComplete
                  ? 'Download your completion certificate'
                  : 'All modules approved — sign declarations and generate certificate'}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        ) : (
          <div className="border-2 border-dashed border-border rounded-xl p-4 flex items-center gap-3">
            <Award className="w-9 h-9 text-muted-foreground" />
            <div>
              <p className="text-sm font-semibold text-foreground">Certificate</p>
              <p className="text-xs text-muted-foreground">
                Complete all modules, quizzes, declarations and practical assessments to earn your certificate
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
